<template>
  <div class="hub-page">
    <!-- Header -->
    <header class="hub-header">
      <div class="header-left">
        <div class="logo">
          <span class="logo-icon">🏨</span>
        </div>
        <h1 class="app-title">Property Management</h1>
      </div>
      <div class="header-right">
        <span class="username">{{ username }}</span>
        <button @click="logout" class="logout-btn">
          Logout
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="hub-content">
      <!-- Welcome Section -->
      <div class="welcome-section">
        <h2>Welcome back, {{ username }}!</h2>
        <p>Select a PMS to access your property data or add a new one.</p>
      </div>

      <!-- Quick Actions -->
      <div class="quick-actions">
        <div class="action-card" @click="showAccountModal = true">
          <span class="action-icon">👤</span>
          <span class="action-text">My Account</span>
        </div>

        <router-link v-if="isSuperAdmin" to="/admin" class="action-card admin-card">
          <span class="action-icon">🛡️</span>
          <span class="action-text">Administration</span>
          <span v-if="pendingUsersCount > 0" class="notification-badge">{{ pendingUsersCount }}</span>
        </router-link>
      </div>

      <!-- PMS List Section -->
      <div class="pms-section">
        <div class="section-header">
          <h3>My Property Management Systems</h3>
          <button @click="showAddPmsModal = true" class="add-pms-btn">
            <span class="btn-icon">+</span>
            Add PMS
          </button>
        </div>

        <!-- PMS Grid -->
        <div v-if="pmsConfigs.length > 0" class="pms-grid">
          <div
            v-for="pms in pmsConfigs"
            :key="pms.id"
            class="pms-card"
          >
            <button
              class="delete-pms-btn"
              @click.stop="confirmDeletePms(pms)"
              title="Delete PMS"
            >
              ×
            </button>
            <div class="pms-card-content" @click="selectPms(pms)">
              <div class="pms-icon">
                <span v-if="pms.pms_type === 'rentlio'">🏠</span>
                <span v-else-if="pms.pms_type === 'booking'">🅱️</span>
                <span v-else-if="pms.pms_type === 'airbnb'">🏡</span>
                <span v-else>📋</span>
              </div>
              <div class="pms-info">
                <h4 class="pms-name">{{ pms.name }}</h4>
                <p class="pms-type">{{ formatPmsType(pms.pms_type) }}</p>
              </div>
              <div class="pms-status" :class="{ 'active': pms.is_active }">
                {{ pms.is_active ? 'Active' : 'Inactive' }}
              </div>
              <div class="pms-arrow">→</div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else class="empty-state">
          <div class="empty-icon">📭</div>
          <h4>No PMS Configured</h4>
          <p>You haven't added any Property Management System yet.</p>
          <button @click="showAddPmsModal = true" class="add-first-pms-btn">
            <span class="btn-icon">+</span>
            Add Your First PMS
          </button>
        </div>
      </div>
    </main>

    <!-- Add PMS Modal -->
    <div v-if="showAddPmsModal" class="modal-overlay" @click.self="showAddPmsModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h3>Add New PMS</h3>
          <button @click="showAddPmsModal = false" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Step 1: Select PMS Type -->
          <div v-if="addPmsStep === 1" class="pms-selection">
            <p class="step-info">Select a Property Management System to connect:</p>
            <div class="pms-options">
              <div
                class="pms-option"
                :class="{ 'selected': selectedPmsType === 'rentlio' }"
                @click="selectedPmsType = 'rentlio'"
              >
                <span class="option-icon">🏠</span>
                <span class="option-name">Rentlio</span>
                <span class="option-check" v-if="selectedPmsType === 'rentlio'">✓</span>
              </div>
              <div
                class="pms-option disabled"
                title="Coming soon"
              >
                <span class="option-icon">🅱️</span>
                <span class="option-name">Booking.com</span>
                <span class="coming-soon">Soon</span>
              </div>
              <div
                class="pms-option disabled"
                title="Coming soon"
              >
                <span class="option-icon">🏡</span>
                <span class="option-name">Airbnb</span>
                <span class="coming-soon">Soon</span>
              </div>
            </div>
            <button
              @click="addPmsStep = 2"
              class="next-btn"
              :disabled="!selectedPmsType"
            >
              Next
            </button>
          </div>

          <!-- Step 2: Enter Credentials -->
          <div v-if="addPmsStep === 2" class="credentials-form">
            <p class="step-info">Enter your {{ formatPmsType(selectedPmsType) }} credentials:</p>

            <div class="form-group">
              <label for="pmsName">Configuration Name</label>
              <input
                type="text"
                id="pmsName"
                v-model="newPmsName"
                placeholder="e.g., My Properties"
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="apiKey">API Key</label>
              <input
                type="password"
                id="apiKey"
                v-model="newPmsApiKey"
                placeholder="Enter your API key"
                class="form-input"
              />
            </div>

            <!-- Error Message -->
            <div v-if="addPmsError" class="error-message">
              {{ addPmsError }}
            </div>

            <!-- Success Message -->
            <div v-if="addPmsSuccess" class="success-message">
              {{ addPmsSuccess }}
            </div>

            <div class="form-actions">
              <button @click="addPmsStep = 1" class="back-btn">Back</button>
              <button
                @click="testConnection"
                class="test-btn"
                :disabled="isTestingConnection || !newPmsName || !newPmsApiKey"
              >
                <span v-if="isTestingConnection" class="loading-spinner"></span>
                {{ isTestingConnection ? 'Testing...' : 'Test Connection' }}
              </button>
              <button
                @click="savePmsConfig"
                class="save-btn"
                :disabled="!connectionTested || isSavingPms"
              >
                <span v-if="isSavingPms" class="loading-spinner"></span>
                {{ isSavingPms ? 'Saving...' : 'Save' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Settings Modal -->
    <div v-if="showAccountModal" class="modal-overlay account-modal-overlay" @click.self="showAccountModal = false">
      <div class="modal-container account-modal">
        <div class="modal-header">
          <h3>My Account</h3>
          <button @click="showAccountModal = false" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <!-- Success/Error Messages -->
          <div v-if="accountSuccessMessage" class="success-message">
            {{ accountSuccessMessage }}
          </div>
          <div v-if="accountErrorMessage" class="error-message">
            {{ accountErrorMessage }}
          </div>

          <!-- Account Information -->
          <div class="account-section">
            <h4>Account Information</h4>
            <div class="account-info-grid">
              <div class="info-item">
                <label>Username</label>
                <span>{{ userProfile.username }}</span>
              </div>
              <div class="info-item">
                <label>Email</label>
                <span>{{ userProfile.email }}</span>
              </div>
              <div class="info-item">
                <label>Status</label>
                <span class="status-badge" :class="userProfile.status">{{ userProfile.status }}</span>
              </div>
            </div>
          </div>

          <!-- Change Password -->
          <div class="password-section">
            <h4>Change Password</h4>
            <div class="password-form">
              <div class="form-group">
                <label for="currentPassword">Current Password</label>
                <div class="input-with-toggle">
                  <input
                    :type="showCurrentPassword ? 'text' : 'password'"
                    id="currentPassword"
                    v-model="passwordForm.currentPassword"
                    class="form-input"
                    placeholder="Enter current password"
                  >
                  <button type="button" class="toggle-password" @click="showCurrentPassword = !showCurrentPassword">
                    <span v-if="showCurrentPassword">👁️</span>
                    <span v-else>👁️‍🗨️</span>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label for="newPassword">New Password</label>
                <div class="input-with-toggle">
                  <input
                    :type="showNewPassword ? 'text' : 'password'"
                    id="newPassword"
                    v-model="passwordForm.newPassword"
                    class="form-input"
                    placeholder="Enter new password (min 8 characters)"
                  >
                  <button type="button" class="toggle-password" @click="showNewPassword = !showNewPassword">
                    <span v-if="showNewPassword">👁️</span>
                    <span v-else>👁️‍🗨️</span>
                  </button>
                </div>
              </div>
              <div class="form-group">
                <label for="confirmNewPassword">Confirm New Password</label>
                <div class="input-with-toggle">
                  <input
                    :type="showConfirmNewPassword ? 'text' : 'password'"
                    id="confirmNewPassword"
                    v-model="passwordForm.confirmNewPassword"
                    class="form-input"
                    placeholder="Confirm new password"
                  >
                  <button type="button" class="toggle-password" @click="showConfirmNewPassword = !showConfirmNewPassword">
                    <span v-if="showConfirmNewPassword">👁️</span>
                    <span v-else>👁️‍🗨️</span>
                  </button>
                </div>
              </div>
              <button
                @click="changePassword"
                class="change-password-btn"
                :disabled="changingPassword || !canChangePassword"
              >
                <span v-if="changingPassword" class="loading-spinner"></span>
                {{ changingPassword ? 'Changing...' : 'Change Password' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal-container delete-modal">
        <div class="modal-header">
          <h3>Delete PMS Configuration</h3>
          <button @click="showDeleteModal = false" class="close-btn">&times;</button>
        </div>

        <div class="modal-body">
          <div class="warning-icon">⚠️</div>
          <p class="warning-text">
            Are you sure you want to delete <strong>{{ pmsToDelete?.name }}</strong>?
          </p>
          <p class="warning-subtext">
            This action cannot be undone. All data associated with this PMS configuration will be permanently removed.
          </p>

          <!-- Error Message -->
          <div v-if="deleteError" class="error-message">
            {{ deleteError }}
          </div>

          <div class="modal-actions">
            <button @click="showDeleteModal = false" class="cancel-btn">
              Cancel
            </button>
            <button
              @click="deletePms"
              class="delete-confirm-btn"
              :disabled="isDeletingPms"
            >
              <span v-if="isDeletingPms" class="loading-spinner"></span>
              {{ isDeletingPms ? 'Deleting...' : 'Delete' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SETTINGS from '@/config/settings.js';

export default {
  name: 'HubView',
  data() {
    return {
      username: '',
      isSuperAdmin: false,
      pendingUsersCount: 0,
      pmsConfigs: [],
      showAddPmsModal: false,
      addPmsStep: 1,
      selectedPmsType: 'rentlio',
      newPmsName: '',
      newPmsApiKey: '',
      addPmsError: '',
      addPmsSuccess: '',
      isTestingConnection: false,
      isSavingPms: false,
      connectionTested: false,
      // Account modal
      showAccountModal: false,
      userProfile: {
        username: '',
        email: '',
        status: ''
      },
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      },
      changingPassword: false,
      accountSuccessMessage: '',
      accountErrorMessage: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmNewPassword: false,
      // Delete modal
      showDeleteModal: false,
      pmsToDelete: null,
      isDeletingPms: false,
      deleteError: ''
    };
  },

  computed: {
    canChangePassword() {
      return (
        this.passwordForm.currentPassword &&
        this.passwordForm.newPassword &&
        this.passwordForm.confirmNewPassword &&
        this.passwordForm.newPassword === this.passwordForm.confirmNewPassword &&
        this.passwordForm.newPassword.length >= 8
      );
    }
  },

  methods: {
    logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.$router.push('/login');
    },

    formatPmsType(type) {
      const types = {
        'rentlio': 'Rentlio',
        'booking': 'Booking.com',
        'airbnb': 'Airbnb'
      };
      return types[type] || type;
    },

    selectPms(pms) {
      // Store selected PMS config
      localStorage.setItem('selectedPmsConfig', JSON.stringify(pms));
      // Redirect to dashboard
      this.$router.push('/dashboard');
    },

    async fetchUserProfile() {
      const token = localStorage.getItem('token');
      if (!token) {
        this.$router.push('/login');
        return;
      }

      try {
        const apiUrl = this.getApiUrl('/api/users/profile/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.username = data.username || data.email || 'User';
          this.isSuperAdmin = data.is_super_admin || false;

          // Update userProfile for modal
          this.userProfile = {
            username: data.username || '',
            email: data.email || '',
            status: data.status || 'active'
          };

          // Store user info
          localStorage.setItem('user', JSON.stringify(data));
        } else if (response.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    },

    async changePassword() {
      if (!this.canChangePassword) return;

      this.changingPassword = true;
      this.accountSuccessMessage = '';
      this.accountErrorMessage = '';

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/users/change-password/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            current_password: this.passwordForm.currentPassword,
            new_password: this.passwordForm.newPassword
          })
        });

        if (response.ok) {
          this.accountSuccessMessage = 'Password changed successfully!';
          this.passwordForm = {
            currentPassword: '',
            newPassword: '',
            confirmNewPassword: ''
          };

          setTimeout(() => {
            this.accountSuccessMessage = '';
          }, 3000);
        } else {
          const data = await response.json();
          this.accountErrorMessage = data.message || data.detail || 'Failed to change password.';
        }
      } catch (error) {
        console.error('Error changing password:', error);
        this.accountErrorMessage = 'Failed to change password. Please try again.';
      } finally {
        this.changingPassword = false;
      }
    },

    async fetchPmsConfigs() {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pmsConfigs = data.results || data || [];
        }
      } catch (error) {
        console.error('Error fetching PMS configs:', error);
      }
    },

    async fetchPendingUsers() {
      if (!this.isSuperAdmin) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const apiUrl = this.getApiUrl('/api/users/pending/');
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.pendingUsersCount = data.length || 0;
        }
      } catch (error) {
        console.error('Error fetching pending users:', error);
      }
    },

    async testConnection() {
      this.addPmsError = '';
      this.addPmsSuccess = '';
      this.isTestingConnection = true;

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/pms/test-connection/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pms_type: this.selectedPmsType,
            credentials: {
              api_key: this.newPmsApiKey
            }
          })
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.connectionTested = true;
          this.addPmsSuccess = 'Connection successful! You can now save this configuration.';
        } else {
          this.addPmsError = data.message || 'Connection failed. Please check your credentials.';
          this.connectionTested = false;
        }
      } catch (error) {
        console.error('Connection test error:', error);
        this.addPmsError = 'Connection test failed. Please try again.';
        this.connectionTested = false;
      } finally {
        this.isTestingConnection = false;
      }
    },

    async savePmsConfig() {
      this.addPmsError = '';
      this.isSavingPms = true;

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl('/api/users/pms-configs/');
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: this.newPmsName,
            pms_type: this.selectedPmsType,
            credentials: {
              api_key: this.newPmsApiKey
            },
            is_active: true
          })
        });

        if (response.ok) {
          // Reset form
          this.showAddPmsModal = false;
          this.addPmsStep = 1;
          this.newPmsName = '';
          this.newPmsApiKey = '';
          this.connectionTested = false;
          this.addPmsSuccess = '';

          // Refresh PMS list
          await this.fetchPmsConfigs();
        } else {
          const data = await response.json();
          this.addPmsError = data.message || data.detail || 'Failed to save configuration.';
        }
      } catch (error) {
        console.error('Save PMS error:', error);
        this.addPmsError = 'Failed to save configuration. Please try again.';
      } finally {
        this.isSavingPms = false;
      }
    },

    confirmDeletePms(pms) {
      this.pmsToDelete = pms;
      this.showDeleteModal = true;
      this.deleteError = '';
    },

    async deletePms() {
      if (!this.pmsToDelete) return;

      this.isDeletingPms = true;
      this.deleteError = '';

      const token = localStorage.getItem('token');

      try {
        const apiUrl = this.getApiUrl(`/api/users/pms-configs/${this.pmsToDelete.id}/`);
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          // Close modal
          this.showDeleteModal = false;
          this.pmsToDelete = null;

          // Refresh PMS list
          await this.fetchPmsConfigs();
        } else {
          const data = await response.json();
          this.deleteError = data.message || data.detail || 'Failed to delete PMS configuration.';
        }
      } catch (error) {
        console.error('Delete PMS error:', error);
        this.deleteError = 'Failed to delete PMS configuration. Please try again.';
      } finally {
        this.isDeletingPms = false;
      }
    },

    getApiUrl(endpoint) {
      return `${SETTINGS.API.baseUrl}${endpoint}`;
    }
  },

  async mounted() {
    await this.fetchUserProfile();
    await this.fetchPmsConfigs();
    await this.fetchPendingUsers();
  }
};
</script>

<style scoped>
.hub-page {
  min-height: 100vh;
  background: #e2e8f0;
}

/* Header */
.hub-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 15px;
}

.logo {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.logo-icon {
  font-size: 24px;
}

.app-title {
  color: white;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.username {
  color: white;
  font-weight: 600;
  font-size: 15px;
}

.logout-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #c53030;
}

/* Main Content */
.hub-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Welcome Section */
.welcome-section {
  text-align: center;
  margin-bottom: 40px;
}

.welcome-section h2 {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px 0;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.welcome-section p {
  font-size: 16px;
  color: #475569;
  margin: 0;
}

/* Quick Actions */
.quick-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
}

.action-card {
  background: white;
  padding: 20px 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
}

.action-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  border-color: #667eea;
}

.action-icon {
  font-size: 24px;
}

.action-text {
  font-size: 15px;
  font-weight: 600;
  color: #1a202c;
}

.admin-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.admin-card .action-text {
  color: white;
}

.notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #e53e3e;
  color: white;
  font-size: 12px;
  font-weight: 700;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* PMS Section */
.pms-section {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.add-pms-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.add-pms-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-icon {
  font-size: 18px;
}

/* PMS Grid */
.pms-grid {
  padding: 20px;
  display: grid;
  gap: 15px;
}

.pms-card {
  position: relative;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: white;
}

.pms-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
}

.pms-card-content {
  display: flex;
  align-items: center;
  padding: 20px 25px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pms-card-content:hover {
  transform: translateX(5px);
}

.delete-pms-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #e53e3e;
  color: white;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  opacity: 0;
  z-index: 10;
}

.pms-card:hover .delete-pms-btn {
  opacity: 1;
}

.delete-pms-btn:hover {
  background: #c53030;
  transform: scale(1.1);
}

.pms-icon {
  font-size: 32px;
  margin-right: 20px;
}

.pms-info {
  flex: 1;
}

.pms-name {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.pms-type {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.pms-status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  margin-right: 15px;
  background: #fee2e2;
  color: #dc2626;
}

.pms-status.active {
  background: #dcfce7;
  color: #16a34a;
}

.pms-arrow {
  font-size: 20px;
  color: #667eea;
  font-weight: bold;
}

/* Empty State */
.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-state h4 {
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 10px 0;
}

.empty-state p {
  font-size: 15px;
  color: #64748b;
  margin: 0 0 25px 0;
}

.add-first-pms-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.add-first-pms-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #1a202c;
}

.close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #64748b;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  transition: color 0.3s ease;
}

.close-btn:hover {
  color: #1a202c;
}

.modal-body {
  padding: 25px;
}

.step-info {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px 0;
}

/* PMS Options */
.pms-options {
  display: grid;
  gap: 12px;
  margin-bottom: 25px;
}

.pms-option {
  display: flex;
  align-items: center;
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pms-option:hover:not(.disabled) {
  border-color: #667eea;
  background: #f8fafc;
}

.pms-option.selected {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
}

.pms-option.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-icon {
  font-size: 24px;
  margin-right: 15px;
}

.option-name {
  flex: 1;
  font-weight: 600;
  color: #1a202c;
}

.option-check {
  color: #667eea;
  font-weight: bold;
  font-size: 18px;
}

.coming-soon {
  font-size: 11px;
  background: #e2e8f0;
  color: #64748b;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.next-btn {
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 14px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
}

.next-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.next-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Credentials Form */
.credentials-form .form-group {
  margin-bottom: 18px;
}

.credentials-form label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 14px;
}

.success-message {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #16a34a;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.back-btn {
  flex: 1;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #e2e8f0;
}

.test-btn {
  flex: 1;
  background: #f0fdf4;
  color: #16a34a;
  border: 2px solid #16a34a;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.test-btn:hover:not(:disabled) {
  background: #16a34a;
  color: white;
}

.test-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-btn {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Account Modal */
.account-modal-overlay {
  background: rgba(0, 0, 0, 0.6);
}

.account-modal {
  width: 80%;
  max-width: 800px;
  max-height: 85vh;
}

.account-section {
  margin-bottom: 30px;
}

.account-section h4,
.password-section h4 {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  padding-bottom: 10px;
  border-bottom: 2px solid #e2e8f0;
}

.account-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-item label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-item span {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
  width: fit-content;
}

.status-badge.approved,
.status-badge.active {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.inactive {
  background: #fee2e2;
  color: #dc2626;
}

.password-section {
  background: #f8fafc;
  border-radius: 12px;
  padding: 25px;
}

.password-form {
  display: grid;
  gap: 15px;
  max-width: 400px;
}

.password-form .form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.password-form label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.password-form .form-input {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.password-form .form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-with-toggle {
  position: relative;
  display: flex;
  align-items: center;
}

.input-with-toggle .form-input {
  padding-right: 45px;
}

.input-with-toggle .toggle-password {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.input-with-toggle .toggle-password:hover {
  opacity: 1;
}

.change-password-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin-top: 10px;
  width: fit-content;
}

.change-password-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.change-password-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .hub-header {
    padding: 15px 20px;
    flex-direction: column;
    gap: 15px;
  }

  .header-left, .header-right {
    width: 100%;
    justify-content: center;
  }

  .app-title {
    font-size: 18px;
  }

  .hub-content {
    padding: 20px 15px;
  }

  .welcome-section h2 {
    font-size: 22px;
  }

  .quick-actions {
    flex-direction: column;
  }

  .action-card {
    justify-content: center;
  }

  .section-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .pms-card {
    flex-wrap: wrap;
  }

  .form-actions {
    flex-direction: column;
  }

  .account-modal {
    width: 95%;
  }

  .account-info-grid {
    grid-template-columns: 1fr;
  }

  .password-form {
    max-width: 100%;
  }
}

/* Delete Modal */
.delete-modal {
  max-width: 450px;
}

.warning-icon {
  font-size: 48px;
  text-align: center;
  margin-bottom: 20px;
}

.warning-text {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
  text-align: center;
  margin-bottom: 10px;
}

.warning-subtext {
  font-size: 14px;
  color: #64748b;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.6;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.cancel-btn {
  flex: 1;
  background: #f1f5f9;
  color: #64748b;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #e2e8f0;
}

.delete-confirm-btn {
  flex: 1;
  background: #e53e3e;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.delete-confirm-btn:hover:not(:disabled) {
  background: #c53030;
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(229, 62, 62, 0.4);
}

.delete-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
